/**
 * Cognitive Engine · Window Store
 * ================================
 * Store globale project-agnostic per finestre.
 * Nessuna dipendenza da Forge — utilizzabile in qualsiasi app React.
 *
 * Modello finestra:
 *   { id, kind, title, props, rect: {x,y,w,h}, z, focused, minimized, closed }
 *
 * Preset di layout: single | split-h | split-v | grid-2x2 | stack
 */
import { useSyncExternalStore } from "react";

const LS_KEY = "cognitive.windows.v1";

let _state = {
  windows: [],           // ordered by z ascending
  layout: "free",        // free | split-h | split-v | grid-2x2 | stack
  nextZ: 1,
};

// Attempt restore from localStorage (safe)
try {
  const raw = typeof window !== "undefined" && window.localStorage.getItem(LS_KEY);
  if (raw) {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.windows)) {
      _state = { ..._state, ...parsed };
    }
  }
} catch { /* no restore */ }

const _listeners = new Set();
function _emit() {
  _listeners.forEach((l) => l());
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LS_KEY, JSON.stringify({
        windows: _state.windows.filter((w) => !w.transient),
        layout: _state.layout,
        nextZ: _state.nextZ,
      }));
    }
  } catch { /* ignore */ }
}
function _subscribe(l) { _listeners.add(l); return () => _listeners.delete(l); }
function _snapshot() { return _state; }

// ── Public API ──────────────────────────────────────────
export const windowStore = {
  getState: _snapshot,
  subscribe: _subscribe,

  open({ id, kind, title, props = {}, rect, transient = false }) {
    const wid = id || `${kind}-${Math.random().toString(36).slice(2, 8)}`;
    const existing = _state.windows.find((w) => w.id === wid);
    if (existing) {
      // bring to front
      this.focus(wid);
      return wid;
    }
    const defaultRect = rect || { x: 80 + Math.random() * 200, y: 80 + Math.random() * 120, w: 480, h: 320 };
    const w = {
      id: wid, kind, title: title || kind, props, rect: defaultRect,
      z: _state.nextZ, focused: true, minimized: false, closed: false,
      transient,
      createdAt: Date.now(),
    };
    _state = {
      ..._state,
      windows: [..._state.windows.map((x) => ({ ...x, focused: false })), w],
      nextZ: _state.nextZ + 1,
    };
    _emit();
    return wid;
  },

  close(id) {
    _state = { ..._state, windows: _state.windows.filter((w) => w.id !== id) };
    _emit();
  },

  focus(id) {
    const found = _state.windows.find((w) => w.id === id);
    if (!found) return;
    _state = {
      ..._state,
      windows: _state.windows.map((w) => ({
        ...w,
        focused: w.id === id,
        z: w.id === id ? _state.nextZ : w.z,
      })),
      nextZ: _state.nextZ + 1,
    };
    _emit();
  },

  move(id, patch) {
    _state = {
      ..._state,
      windows: _state.windows.map((w) => w.id === id
        ? { ...w, rect: { ...w.rect, ...patch } }
        : w),
    };
    _emit();
  },

  resize(id, patch) { this.move(id, patch); },

  minimize(id) {
    _state = {
      ..._state,
      windows: _state.windows.map((w) => w.id === id ? { ...w, minimized: !w.minimized } : w),
    };
    _emit();
  },

  setLayout(name) {
    _state = { ..._state, layout: name };
    this.arrange(name);
  },

  arrange(preset) {
    const active = _state.windows.filter((w) => !w.minimized);
    const n = active.length;
    if (n === 0) return;
    const viewport = { w: (typeof window !== "undefined" ? window.innerWidth : 1600) - 40,
                       h: (typeof window !== "undefined" ? window.innerHeight : 900) - 100 };
    const pad = 12;
    const arranged = [];
    if (preset === "split-h" && n >= 2) {
      const cw = (viewport.w - pad) / 2;
      arranged.push({ id: active[0].id, rect: { x: 20, y: 60, w: cw, h: viewport.h } });
      arranged.push({ id: active[1].id, rect: { x: 20 + cw + pad, y: 60, w: cw, h: viewport.h } });
    } else if (preset === "split-v" && n >= 2) {
      const ch = (viewport.h - pad) / 2;
      arranged.push({ id: active[0].id, rect: { x: 20, y: 60, w: viewport.w, h: ch } });
      arranged.push({ id: active[1].id, rect: { x: 20, y: 60 + ch + pad, w: viewport.w, h: ch } });
    } else if (preset === "grid-2x2") {
      const cw = (viewport.w - pad) / 2;
      const ch = (viewport.h - pad) / 2;
      active.slice(0, 4).forEach((w, i) => {
        const col = i % 2, row = Math.floor(i / 2);
        arranged.push({ id: w.id, rect: {
          x: 20 + col * (cw + pad),
          y: 60 + row * (ch + pad),
          w: cw, h: ch,
        }});
      });
    } else if (preset === "stack") {
      active.forEach((w, i) => {
        arranged.push({ id: w.id, rect: {
          x: 20 + i * 24, y: 60 + i * 24, w: 640, h: 420,
        }});
      });
    }
    if (arranged.length) {
      _state = {
        ..._state, layout: preset,
        windows: _state.windows.map((w) => {
          const found = arranged.find((a) => a.id === w.id);
          return found ? { ...w, rect: found.rect } : w;
        }),
      };
      _emit();
    }
  },

  closeAll() {
    _state = { ..._state, windows: [] };
    _emit();
  },
};

// ── React hook ──────────────────────────────────────────
export function useWindows() {
  return useSyncExternalStore(_subscribe, _snapshot, _snapshot);
}
